import { useMemo } from "react";
import {
  useGetDataPackageForTemplateEditorQuery,
  useGetQueriesForTemplateEditorQuery,
} from "~/graphql/supergraphHooks";
import type { DataPackageOption } from "../components/GeneralSettingsStep";
import type { SupportedTemplateType } from "~/graphql/supergraphTypes";

export type ReportingQueryOption = {
  id: string;
  name: string;
};

type UseReportingQueriesArgs = {
  dataPackageId: string;
  dataPackageOptions: DataPackageOption[];
  /** Arena route context (e.g. Kanto template). Widens query list to arena + system. */
  viewerArenaId?: number | null;
  /**
   * When provided, narrow `queryOptions` to queries whose `supportedTemplateType` matches.
   * - `undefined`: no filtering (show all queries)
   * - `null`: template type is selected but has no compatible query type (show no queries)
   * - `SupportedTemplateType`: filter to matching queries only
   */
  supportedTemplateType?: SupportedTemplateType | null;
};

const parseDataPackageId = (raw: string): number | null => {
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

export const useReportingQueries = ({
  dataPackageId,
  dataPackageOptions,
  viewerArenaId,
  supportedTemplateType = undefined,
}: UseReportingQueriesArgs) => {
  const parsedPackageId = useMemo(() => parseDataPackageId(dataPackageId), [dataPackageId]);

  const selectedPackage = useMemo(
    () => dataPackageOptions.find((pkg) => String(pkg.id) === dataPackageId),
    [dataPackageId, dataPackageOptions],
  );

  const packageLookupArenaId = viewerArenaId ?? selectedPackage?.arenaId ?? null;

  const { data: packageData, loading: isPackageLoading } =
    useGetDataPackageForTemplateEditorQuery({
      variables: {
        arenaId: packageLookupArenaId,
        dataPackageId: parsedPackageId ?? 0,
      },
      skip: parsedPackageId == null,
      fetchPolicy: "cache-and-network",
    });

  const dataPackageVersionId = packageData?.finpubDataPackage?.currentVersion?.id ?? null;
  const packageArenaId = packageData?.finpubDataPackage?.arenaId ?? null;
  const queriesArenaId = viewerArenaId ?? packageArenaId;

  const { data: queriesData, loading: isQueriesLoading } = useGetQueriesForTemplateEditorQuery({
    variables: {
      arenaId: queriesArenaId,
      dataPackageVersionId: dataPackageVersionId!,
      includeInactive: false,
    },
    skip: dataPackageVersionId == null,
    fetchPolicy: "cache-and-network",
  });

  const queryOptions = useMemo<ReportingQueryOption[]>(
    () =>
      (queriesData?.finpubQueries ?? [])
        .filter((query) => query.isActive !== false)
        .filter(
          (query) =>
            supportedTemplateType === undefined ||
            query.supportedTemplateType === supportedTemplateType,
        )
        .map((query) => ({
          id: String(query.id),
          name: query.name,
        })),
    [queriesData, supportedTemplateType],
  );

  const queriesResolved = useMemo(() => {
    if (parsedPackageId == null) return false;
    if (isPackageLoading) return false;
    if (dataPackageVersionId == null) return true;
    return !isQueriesLoading;
  }, [parsedPackageId, isPackageLoading, dataPackageVersionId, isQueriesLoading]);

  return {
    queryOptions,
    queriesLoading: isPackageLoading || isQueriesLoading,
    queriesResolved,
    packageArenaId,
    /** Same arena used to load queries (`viewerArenaId ?? packageArenaId`). */
    queriesArenaId,
  };
};
